<template>
    <v-container fluid>
        <v-card flat>
            <v-card-title>
                <router-link
                    to="/settings/auto-respond-rules"
                    class="text-decoration-none"
                >
                    <v-btn icon>
                        <v-icon>chevron_left</v-icon>
                    </v-btn>
                </router-link>
                {{ editMode ? ruleName : 'Add Auto-Respond Rule' }}
                <v-spacer />
                <v-btn
                    v-if="editMode"
                    color="red darken-1"
                    outlined
                    style="font-size: small;text-transform: none;"
                    @click="showDeleteDialog=true"
                >
                    <v-icon left>
                        delete
                    </v-icon>
                    Delete
                </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="text-md-body-1 text--primary px-5 my-2">
                Rules must use at least one criteria be utilized. Response Codes
                for Design/Engineering tickets are not eligible for Auto-Response.
            </v-card-text>
            <v-form
                ref="formAddRule"
                v-model="formValid"
                class="px-5"
            >
                <v-row class="mb-2">
                    <v-col cols="auto">
                        <v-checkbox
                            v-model="ruleEnabled"
                            label="Enabled"
                            hide-details
                            dense
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleName"
                            :rules="requiredRule"
                            label="Rule Name*"
                            outlined
                            dense
                            required
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-select
                            v-model="selectedCenter"
                            :items="companyCenters"
                            :disabled="companyCenters.length === 1"
                            item-text="center_abbrev"
                            item-value="center_id"
                            :rules="requiredRule"
                            label="Center*"
                            outlined
                            dense
                            required
                            @change="changeCenter"
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-select
                            ref="selectState"
                            v-model="selectedState"
                            :items="centerStates"
                            :rules="requiredRule"
                            :disabled="(selectedState === '' && centerStates.length === 0) || (selectedState !== '' && centerStates.length === 1)"
                            label="State*"
                            outlined
                            dense
                            required
                            @change="changeState"
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-select
                            v-model="selectedResponseCode"
                            :items="formattedResponseCodes"
                            item-text="text"
                            item-value="value"
                            :rules="requiredRule"
                            label="Response Code*"
                            :disabled="selectedCenter === '' || selectedState === ''"
                            outlined
                            dense
                            required
                            @change="changeResposeCode"
                        >
                            <template v-slot:item="{ item }">
                                <div class="response-select-item">
                                    {{ item.text }}
                                </div>
                            </template>
                        </v-select>
                    </v-col>
                    <v-col
                        v-if="responseCommentField.show"
                        cols="12"
                    >
                        <v-textarea 
                            v-model="responseComment"
                            :label="`+ Positive Response Comments${responseCommentField.required?'*':''}`"
                            outlined
                            no-resize
                            :required="responseCommentField.required"
                            :rules="responseCommentField.required ? [v => (!!v || 'Response Comment is required')] : []"
                        />
                    </v-col>
                    <v-col
                        v-if="responseUrlField.show"
                        cols="12"
                    >
                        <v-text-field
                            v-model="responseUrl"
                            :label="`+ Positive Response URL${responseUrlField.required?'*':''}`"
                            outlined
                            dense
                            :required="responseUrlField.required"
                            :rules="responseUrlField.required ? [v => (!!v || 'Response Comment is required'), validateUrl] : [validateUrl]"
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-autocomplete
                            v-model="selectedLocator"
                            :items="companyLocators"
                            item-text="text"
                            item-value="value"
                            label="Assignment"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-slider
                            v-model="ruleWeight"
                            class="align-center"
                            :max="weightMax"
                            :min="weightMin"
                            label="Weight"
                            inverse-label
                            hide-details
                            color="orange darken-3"
                            track-color="grey lighten-3"
                        >
                            <template v-slot:append>
                                <v-text-field
                                    v-model="ruleWeight"
                                    class="mt-0 pt-0"
                                    outlined
                                    dense
                                    hide-details
                                    single-line
                                    type="number"
                                    :max="weightMax"
                                    :min="weightMin"
                                    style="width: 65px"
                                />
                            </template>
                        </v-slider>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <h3>Criteria</h3>
                        <v-divider class="mt-2" />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        cols="12"
                    >
                        <v-select
                            v-model="selectedMemberCode"
                            :items="centerMemberCodes"
                            :disabled="selectedCenter === ''"
                            item-text="text"
                            item-value="value"
                            label="Member Code*"
                            outlined
                            multiple
                            small-chips
                            required
                            :rules="requiredArrayRule"
                            @change="changeMemberCode"
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-select
                            v-model="selectedTicketPriority"
                            :items="centerTicketPriorities"
                            :disabled="selectedCenter === ''"
                            item-text="text"
                            item-value="value"
                            label="Priority"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-select
                            v-model="selectedTicketType"
                            :items="centerTicketTypes"
                            :disabled="selectedCenter === ''"
                            label="Type"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('ticket_category')"
                        cols="12"
                        md="6"
                    >
                        <v-select
                            v-model="selectedTicketCategory"
                            :items="centerTicketCategories"
                            :disabled="selectedCenter === ''"
                            item-text="text"
                            item-value="value"
                            label="Category"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        cols="4"
                        md="2"
                    >
                        <v-select
                            v-model="selectedMapLayerOper"
                            dense
                            :items="layerIntersectionType"
                            item-text="label"
                            item-value="value"
                            outlined
                            label="Layer Operation"
                            hide-details="true"
                        />
                    </v-col>
                    <v-col
                        cols="8"
                        md="4"
                    >
                        <v-select
                            v-model="selectedMapLayer"
                            :items="mapLayers"
                            item-text="name"
                            item-value="layer_id"
                            label="Map Layer"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-select
                            v-model="selectedCounties"
                            :items="stateCounties"
                            :disabled="selectedState === ''"
                            item-text="option"
                            item-value="fips"
                            label="County"
                            multiple
                            small-chips
                            outlined
                            @change="changeCounties"
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="rulePlace"
                            label="Place"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('subdivision')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleSubdivision"
                            label="Subdivision"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleStreet"
                            label="Street"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('company')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleCompany"
                            label="Company"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('donefor')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleDoneFor"
                            label="Done For"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('doneby')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleDoneBy"
                            label="Done By"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('caller')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleCaller"
                            label="Caller"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('caller_phone')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleCallerPhone"
                            label="Caller Phone"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('work_type')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleWorkType"
                            label="Work Type"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('location')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleLocation"
                            label="Location"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('keywords')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleKeywords"
                            label="Keywords"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('grid')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleGrid"
                            label="Grid"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('instructions')"
                        cols="12"
                        md="6"
                    >
                        <v-text-field
                            v-model="ruleInstructions"
                            label="Instructions"
                            outlined
                            dense
                        />
                    </v-col>
                    <v-col
                        v-if="showField('remarks')"
                        cols="12"
                    >
                        <v-text-field
                            v-model="ruleRemarks"
                            label="Comments/Remarks"
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row v-if="showField('meet')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Meet
                        </span>
                        <v-radio-group
                            v-model="ruleMeet"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('excav_st_sidewalk')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Excav St/Sidewalk
                        </span>
                        <v-radio-group
                            v-model="ruleExcavationStSidewalk"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('boring')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Boring
                        </span>
                        <v-radio-group
                            v-model="ruleBoring"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('explosives')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Explosives
                        </span>
                        <v-radio-group
                            v-model="ruleExplosives"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('vacuum')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Vacuum
                        </span>
                        <v-radio-group
                            v-model="ruleVacuum"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('remark')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Re-Mark
                        </span>
                        <v-radio-group
                            v-model="ruleRemark"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('delineated')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Delineated
                        </span>
                        <v-radio-group
                            v-model="ruleDelineated"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('weekend')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Weekend
                        </span>
                        <v-radio-group
                            v-model="ruleWeekend"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
                <v-row v-if="showField('night')">
                    <v-col
                        cols="12"
                    >
                        <span class="text-md-body-1 text--primary mx-1">
                            Night
                        </span>
                        <v-radio-group
                            v-model="ruleNight"
                            row
                            class="mt-0"
                        >
                            <v-radio
                                label="Any"
                                value="*"
                            />
                            <v-radio
                                label="Yes"
                                value="Y"
                            />
                            <v-radio
                                label="No"
                                value="N"
                            />
                        </v-radio-group>
                    </v-col>
                </v-row>
            </v-form>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    style="text-transform: none !important"
                    color="primary"
                    outlined
                    @click="cancelEditRule"
                >
                    Cancel
                </v-btn>
                <v-btn
                    style="text-transform: none !important"
                    color="primary"
                    :disabled="disableAddButton"
                    depressed
                    @click="submit"
                >
                    {{ editMode ? 'Update' : 'Add' }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-dialog
            v-model="showDeleteDialog"
            :fullscreen="$vuetify.breakpoint.xs"
            persistent
            scrollable
            width="400"
        >
            <v-card>
                <v-card-title>
                    Delete
                </v-card-title>
                <v-divider />
                <v-card-text>
                    Are you sure you want to delete {{ ruleName }} from the Auto Respond Rules?
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        outlined
                        :width="$vuetify.breakpoint.xs ? '50%' : ''"
                        @click="showDeleteDialog=false"
                    >
                        Close
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        :width="$vuetify.breakpoint.xs ? '50%' : ''"
                        @click="deleteRule"
                    >
                        Delete
                    </v-btn>
                    <v-spacer v-if="this.$vuetify.breakpoint.xs" />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>
<script>
import webtmsBackend from '@/services/webtmsBackend';
import { htmlEncode } from '../../../utils/encoding';
export default ({
    data() {
        return {
            showDeleteDialog: false,
            editMode: false,
            selectedRowid: '',
            ruleEnabled: true,
            ruleName: '',
            selectedCenter: '',
            selectedState: '',
            selectedResponseCode: '',
            responseComment: '',
            responseUrl: '',
            selectedCounties: ['*'],
            selectedMemberCode: ['*'],
            selectedTicketType: '*',
            selectedTicketPriority: '*',
            selectedTicketCategory: '*',
            selectedMapLayer: '',
            selectedMapLayerOper: '',
            selectedLocator: '',
            rulePlace: '',
            ruleSubdivision: '',
            ruleStreet: '',
            ruleCompany: '',
            ruleDoneFor: '',
            ruleDoneBy: '',
            ruleCaller: '',
            ruleCallerPhone: '',
            ruleWorkType: '',
            ruleLocation: '',
            ruleKeywords: '',
            ruleGrid: '',
            ruleInstructions: '',
            ruleRemarks: '',
            ruleWeight: 1,
            ruleMeet: '*',
            ruleExcavationStSidewalk:'*',
            ruleBoring: '*',
            ruleExplosives: '*',
            ruleVacuum: '*',
            ruleRemark: '*',
            ruleDelineated: '*',
            ruleWeekend: '*',
            ruleNight: '*',
            formValid: false,
            valid: false,
            weightMin: 1,
            weightMax: 100,
            requiredRule: [v => !!v || 'Field is required'],
            requiredArrayRule: [v => !!v.length || 'Field is required'],
            layerIntersectionType: [{ value: '', label: 'Intersects'}, { value: 'NOT', label: 'Does NOT Intersect'}],
            debounce: false,
            urlRegEx: '^(https?:\\/\\/)' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$',
            responseCommentField: {
                show: false,
                required: false,
            },
            responseUrlField: {
                show: false,
                required: false,
            },
        };
    },
    computed: {
        companyCenterOptions() {
            if(!this.selectedCenter) {
                return [];
            }
            return this.$store.getters.companyCenters.find(center => {
                return center.center_id === this.selectedCenter;
            }).__OPTIONS__;
        },
        ticketRuleFieldsList() {
            let criteria = this.companyCenterOptions.find(element => element.option === 'TICKET_RULE_FIELDS_LIST');
            if(criteria) {
                return criteria.value.split(',');
            }
            return [];
        },
        ticketRuleFields() {
            let criteria = this.companyCenterOptions.find(element => element.option === 'TICKET_RULE_FIELDS');
            if(criteria) {
                return criteria.value.split(',');
            }
            return [];
        },
        companyLocators() {
            let result = [
                { text: 'Do Not Assign', value: '{00000000-0000-0000-0000-000000000000}' },
            ];
            let companyUsers = this.$store.getters.companyUsers.filter(element => element.ingroup === 'Locator');
            companyUsers.forEach(element => {
                result.push({ text: `${element.account} "${element.name}"`, value: element.uid });
            });
            return result;
        },
        companyCenters() {
            return this.$store.getters.companyCenters;
        },
        centerStates() {
            if (!Number(this.selectedCenter)) {
                return [];
            }
            const center_id = Number(this.selectedCenter); // ensure it is a number
            // uses _DATA.counties to determine the distinct states
            var states = []; // list of state abbreviations e.g. ["CA","NV"]
            const counties = this.$store.getters.counties;
            // get list of distinct state abbreviations
            for(let county of counties) {
                let countyCenter = county[0];
                let countyState = county[2];
                if(countyCenter === center_id && !states.includes(countyState)) {
                    states.push(countyState);
                }
            }
            // return sorted
            return states.sort();
        },
        stateCounties() {
            const allCounties = this.$store.getters.counties;
            let stateCounties = [{
                option: 'Any',
                fips: '*',
            }];
            for(let county of allCounties) {
                if(county[0] === this.selectedCenter && county[2] === this.selectedState) {
                    stateCounties.push({
                        center_id: county[0],
                        fips: county[1],
                        state: county[2],
                        county: county[3],
                        option: `${county[3]}, ${county[2]} (${county[1]})`,
                    });                    
                }
            }
            return stateCounties;
        },
        formattedResponseCodes() {
            let responseCodes = [];
            for(let responseCode of this.$store.getters.companyResponseCodes ) {
                if(!responseCode.design && !this.requiresUnsupportedResponseField(responseCode.response_code)) {
                    responseCodes.push({
                        value: htmlEncode(responseCode.response_code),
                        text: htmlEncode(responseCode.response_code === '---' ? responseCode.description : responseCode.response_code + ' - ' + responseCode.description),
                    });
                }
            }
            return responseCodes;
        },
        centerMemberCodes(){
            if(this.selectedCenter === '') {
                return [];
            }
            let centerMemberCodes = [];
            centerMemberCodes.push({
                text: 'Any',
                value: '*'
            });
            const companyMemberCodes = this.$store.getters.companyMemberCodes;
            for(let memberCode of companyMemberCodes) {
                if (memberCode.center_id === this.selectedCenter) {
                    centerMemberCodes.push({
                        text: `${memberCode.member_code} = ${memberCode.description}`,
                        value: memberCode.member_code
                    });
                }
            }
            return centerMemberCodes;
        },
        centerTicketTypes() {
            if(this.selectedCenter === '') {
                return [];
            }
            let centerTicketTypes = [
                { text: 'Any', value: '*' },
            ];
            const companyTicketTypes = this.$store.getters.companyTicketTypes;
            for(let ticketType of companyTicketTypes) {
                if (ticketType.center_id === this.selectedCenter && ticketType.abbrev !== '') {
                    centerTicketTypes.push({
                        text: `${ticketType.abbrev} = ${ticketType.description}`,
                        value: ticketType.abbrev
                    });
                }
            }
            return centerTicketTypes;
        },
        centerTicketCategories() {
            if(this.selectedCenter === '') {
                return [];
            }
            let centerTicketCategories = [
                { text: 'Any', value: '*' },
            ];
            const companyTicketCategories = this.$store.getters.companyTicketCategories;
            for(let ticketCategory of companyTicketCategories) {
                if (ticketCategory.center_id === this.selectedCenter && ticketCategory.abbrev !== '') {
                    centerTicketCategories.push({
                        text: `${ticketCategory.abbrev} = ${ticketCategory.description}`,
                        value: ticketCategory.abbrev
                    });
                }
            }
            return centerTicketCategories;
        },
        centerTicketPriorities() {
            if(this.selectedCenter === '') {
                return [];
            }
            let centerTicketPriorities = [
                { text: 'Any', value: '*' },
            ];
            const companyTicketPriorities = this.$store.getters.companyTicketPriorities;
            for(let ticketPriority of companyTicketPriorities) {
                if (ticketPriority.center_id === this.selectedCenter && ticketPriority.abbrev !== '') {
                    centerTicketPriorities.push({
                        text: `${ticketPriority.abbrev} = ${ticketPriority.description}`,
                        value: ticketPriority.abbrev
                    });
                }
            }
            return centerTicketPriorities;
        },
        mapLayers() {
            return [
                { name: 'No Layer', layer_id: '{00000000-0000-0000-0000-000000000000}' },
                ...this.$store.getters.companyLayers
            ];
        },
        disableAddButton() {
            return !this.formValid;
        }
    },
    mounted() {
        if(this.companyCenters.length === 1) {
            this.selectedCenter = this.companyCenters[0].center_id;
        }
        if(this.centerStates.length === 1) {
            this.selectedState = this.centerStates[0];
        }
        this.selectedLocator = this.companyLocators[0].value; // default: Do Not Assign
        this.selectedMapLayer = this.mapLayers[0].layer_id; // default: No Layer
        if(this.$route.path.includes('/edit')) {
            this.setSelectedRule();
        }
    },
    methods: {
        changeMemberCode(change) {
            if(change.length > 1) {
                if(change[change.length-1] === '*') {
                    this.selectedMemberCode = ['*'];
                } else if(change.join('').length > 160) {
                    this.$store.dispatch('setSnackbar', { color:'error', text: 'You can select maximum of 160 characters of member codes.', timeout:3000 });
                    this.selectedMemberCode.pop();
                } else {
                    this.selectedMemberCode = change.filter(item => (item !== '*'));
                }
            }
        },
        changeCounties(change) {
            if(change.length > 1) {
                if(change[change.length-1] === '*') {
                    this.selectedCounties = ['*'];
                } else if(change.length > 16) {
                    this.$store.dispatch('setSnackbar', { color:'error', text: 'You can select a maximum of 16 counties', timeout:3000 });
                    this.selectedCounties.pop();
                } else {
                    this.selectedCounties = change.filter(item => (item !== '*'));
                }
            }
        },
        parseFipsCodes(codes) {
            let selectedCodes = codes.match(/.{1,5}/g);
            if(!selectedCodes) {
                selectedCodes =  ['*'];
            }
            return selectedCodes;
        },
        setSelectedRule() {
            let selectedRule = this.$store.getters.selectedAutoResponseRule;
            this.selectedRowid = selectedRule.rowid;
            this.selectedLocator = selectedRule.uid;
            this.ruleName = selectedRule.name;
            this.selectedResponseCode = selectedRule.response_code;
            this.ruleWeight = selectedRule.weight;
            this.ruleEnabled = selectedRule.enabled;
            this.selectedCenter = selectedRule.center_id;
            this.selectedMemberCode = selectedRule.member_code.split(',');
            this.selectedTicketPriority = selectedRule.ticket_priority;
            this.selectedTicketType = selectedRule.ticket_type;
            this.selectedTicketCategory = selectedRule.ticket_category;
            this.selectedMapLayer = selectedRule.layer_id;
            this.selectedMapLayerOper = selectedRule.layer_oper;
            this.ruleMeet = selectedRule.meet;
            this.ruleExcavationStSidewalk = selectedRule.excav_st_sidewalk;
            this.ruleBoring = selectedRule.boring;
            this.ruleExplosives = selectedRule.explosives;
            this.ruleVacuum = selectedRule.vacuum;
            this.ruleRemark = selectedRule.remark;
            this.ruleDelineated = selectedRule.delineated;
            this.ruleWeekend = selectedRule.weekend;
            this.ruleNight = selectedRule.night;
            this.selectedCounties = this.parseFipsCodes(selectedRule.fips);
            this.rulePlace = selectedRule.place;
            this.selectedState = selectedRule.state;
            this.ruleSubdivision = selectedRule.subdivision;
            this.ruleStreet = selectedRule.street;
            this.ruleGrid = selectedRule.grid;
            this.ruleKeywords = selectedRule.keywords;
            this.ruleCompany = selectedRule.company;
            this.ruleDoneBy = selectedRule.doneby;
            this.ruleDoneFor = selectedRule.donefor;
            this.ruleCaller = selectedRule.caller;
            this.ruleCallerPhone = selectedRule.caller_phone;
            this.ruleWorkType = selectedRule.work_type;
            this.ruleLocation = selectedRule.location;
            this.ruleRemarks = selectedRule.remarks;
            this.responseComment = selectedRule.response_comment;
            this.responseUrl = selectedRule.response_url;
            this.ruleInstructions = selectedRule.instructions;
            this.editMode = true;
        },
        getPayload() {
            return {
                cid: this.$store.getters.user.cid,
                uid: this.selectedLocator || '{00000000-0000-0000-0000-000000000000}',
                name: this.ruleName,
                response_code: this.selectedResponseCode,
                weight: this.ruleWeight,
                enabled: this.ruleEnabled,
                center_id: this.selectedCenter,
                member_code: this.selectedMemberCode.join(','),
                ticket_priority: this.selectedTicketPriority,
                ticket_type: this.selectedTicketType,
                ticket_category: this.selectedTicketCategory,
                layer_id: this.selectedMapLayer || '{00000000-0000-0000-0000-000000000000}',
                layer_oper: this.selectedMapLayerOper,
                meet: this.ruleMeet,
                excav_st_sidewalk: this.ruleExcavationStSidewalk,
                boring: this.ruleBoring,
                explosives: this.ruleExplosives,
                vacuum: this.ruleVacuum,
                remark: this.ruleRemark,
                delineated: this.ruleDelineated,
                weekend: this.ruleWeekend,
                night: this.ruleNight,
                fips: this.selectedCounties.join(''),
                place: this.rulePlace,
                state: this.selectedState,
                subdivision: this.ruleSubdivision,
                street: this.ruleStreet,
                grid: this.ruleGrid,
                keywords: this.ruleKeywords,
                company: this.ruleCompany,
                doneby: this.ruleDoneBy,
                donefor: this.ruleDoneFor,
                caller: this.ruleCaller,
                caller_phone: this.ruleCallerPhone,
                work_type: this.ruleWorkType,
                location: this.ruleLocation,
                remarks: this.ruleRemarks,
                response_comment: this.responseComment,
                response_url: this.responseUrl,
                instructions: this.ruleInstructions,
            };
        },
        showField(field) {
            return this.ticketRuleFields.includes(field);
        },
        validateUrl(v) {
            let regExString = this.urlRegEx;
            if(!this.responseUrlField.required) {
                regExString = '^$|' + regExString;
            }
            return new RegExp(regExString, 'i').test(v) || 'URL invalid.'; 
        },
        changeCenter() {
            if(this.centerStates.length === 1) {
                this.selectedState = this.centerStates[0];
            } else {
                this.selectedState = '';
            }
            this.selectedCounties = ['*'];
            this.selectedMemberCode = ['*'];
            this.selectedTicketType = '*';
            this.selectedTicketCategory = '*';
            this.selectedTicketPriority = '*';
            this.selectedResponseCode = '';
            this.changeResposeCode('');
        },
        changeState() {
            this.selectedCounties = ['*'];
        },
        changeResposeCode(responseCode) {
            this.responseCommentField.show = false;
            this.responseCommentField.required = false;
            this.responseUrlField.show = false;
            this.responseUrlField.required = false;
            const {inputFields, requiredFields} = this.getResponseCodeFields(responseCode);
            for(let field of inputFields) {
                switch(field) {
                    case 'comment':
                        this.responseCommentField.show = true;
                        break;
                    case 'url':
                        this.responseUrlField.show = true;
                        break;
                    default:
                        break;
                }
            }
            for(let field of requiredFields) {
                switch(field) {
                    case 'comment':
                        this.responseCommentField.required = true;
                        break;
                    case 'url':
                        this.responseUrlField.required = true;
                        break;
                    default:
                        break;
                }
            }
        },
        getResponseCodeFields(responseCode) {
            let result = {
                inputFields: [],
                requiredFields: [],
            };
            const posRespMap = this.getPositiveResponseMapping(responseCode);
            if (posRespMap) {
                // strip all spaces from string
                posRespMap.pr_input_fields = posRespMap.pr_input_fields.replace(/ /g, '');
                let inputFields = posRespMap.pr_input_fields.split(',');
                if (inputFields[0] === '') {
                    inputFields = [];
                }
                result.inputFields = inputFields;
                posRespMap.pr_required_fields = posRespMap.pr_required_fields.replace(/ /g, '');
                let requiredFields = posRespMap.pr_required_fields.split(',');
                if (requiredFields[0] === '') {
                    requiredFields = [];
                }
                result.requiredFields = requiredFields;
            }
            return result;
        },
        getPositiveResponseMapping (code) {
            return this.$store.getters.companyPositiveResponseMap.find(map => {
                if(map.center_id === this.selectedCenter
                && map.response_code === code
                && map.state === this.selectedState) {
                    return true;
                }
            });
        },
        requiresUnsupportedResponseField(code) {
            const supportedFields = [
                'comment',
                'url',
            ];
            const { requiredFields } = this.getResponseCodeFields(code);
            for(let field of requiredFields) {
                if(!supportedFields.includes(field)) {
                    return true;
                }
            }
            return false;
        },
        async submit() {
            this.debounce = true;
            this.$refs.formAddRule.validate();
            if (this.formValid) {
                let request = this.getPayload();
                if(this.editMode) {
                    request.rowid = this.selectedRowid;
                }
                // Add a timestamp to request object
                request['__TIMESTAMP__'] = new Date().getTime();
                // Change number values to strings
                request.weight = request.weight.toString();
                request.center_id = request.center_id.toString();
                try {
                    await webtmsBackend.updateAutoResponseRules(request);
                    this.$store.commit('SET_SELECTED_AUTO_RESPONSE_RULE', {});
                    this.$router.go(-1);
                } catch (error) {
                    this.debounce = false;
                    return;
                }
            }
        },
        cancelEditRule() {
            this.$store.commit('SET_SELECTED_AUTO_RESPONSE_RULE', {});
            this.$router.go(-1);
        },
        async deleteRule() {
            this.debounce = true;
            try {
                await webtmsBackend.deleteAutoResponseRule({
                    rowid: this.selectedRowid,
                });
                this.$store.commit('SET_SELECTED_AUTO_RESPONSE_RULE', {});
                this.$router.go(-1);
            } catch (error) {
                this.debounce = false;
                return;
            }
            this.showDeleteDialog = false;
        },
    },
});
</script>
<style>
.response-select-item {
    max-width: 410px;
    padding-bottom: 8px;
}
</style>